'use client';

import { useCallback, useSyncExternalStore } from 'react';
import { getBrowserWindow } from '@/lib/utils/browser-window';

type BannerStatus = 'pending' | 'ready';

type BannerEntry = {
  status: BannerStatus;
  listeners: Set<() => void>;
};

const bannerStore = new Map<string, BannerEntry>();

const ensureBannerEntry = (bannerUrl: string): BannerEntry => {
  let entry = bannerStore.get(bannerUrl);
  if (!entry) {
    const browserWindow = getBrowserWindow();
    const initialStatus: BannerStatus = browserWindow ? 'pending' : 'ready';
    entry = { status: initialStatus, listeners: new Set() };
    bannerStore.set(bannerUrl, entry);

    if (initialStatus === 'pending') {
      const ImageConstructor = browserWindow?.Image ?? null;
      if (!ImageConstructor) {
        entry.status = 'ready';
        return entry;
      }
      const img = new ImageConstructor();
      img.decoding = 'async';
      img.src = bannerUrl;
      const finalize = () => {
        if (!entry) {
          return;
        }
        entry.status = 'ready';
        for (const listener of entry.listeners) {
          listener();
        }
      };
      img.addEventListener('load', finalize);
      img.addEventListener('error', finalize);
    }
  } else if (entry.status === 'pending' && !getBrowserWindow()) {
    entry.status = 'ready';
  }
  return entry;
};

const subscribeToBanner = (bannerUrl: string, listener: () => void) => {
  const entry = ensureBannerEntry(bannerUrl);
  entry.listeners.add(listener);
  return () => {
    entry.listeners.delete(listener);
  };
};

const getBannerReady = (bannerUrl: string): boolean => {
  const entry = bannerStore.get(bannerUrl);
  if (!entry) {
    return !getBrowserWindow();
  }
  return entry.status === 'ready';
};

export function useBannerReady(bannerUrl: string | null): boolean {
  const subscribe = useCallback(
    (listener: () => void) => {
      if (!bannerUrl) {
        return () => {
          /* nothing to unsubscribe */
        };
      }
      return subscribeToBanner(bannerUrl, listener);
    },
    [bannerUrl]
  );

  const getSnapshot = useCallback(() => {
    if (!bannerUrl) {
      return true;
    }
    return getBannerReady(bannerUrl);
  }, [bannerUrl]);

  const getServerSnapshot = useCallback(() => true, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
